import { useState, useEffect } from "react";
import { useLocation, useRoute } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Loader2, ChevronLeft, CheckCircle2 } from "lucide-react";
import { DashboardHeader } from "@/components/dashboard/dashboard-header";
import { DashboardShell } from "@/components/dashboard/dashboard-shell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useSelectedBusiness } from "@/hooks/use-selected-business";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { LegalDocumentTemplate } from "@shared/operations-schema";

export default function CreateLegalDocumentPage() {
  const [location] = useLocation();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { activeBusiness: selectedBusiness } = useSelectedBusiness();
  const [selectedTemplate, setSelectedTemplate] = useState<LegalDocumentTemplate | null>(null);
  const [isFormStep, setIsFormStep] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Extract type from URL path
  const type = decodeURIComponent(location.split('/legal/create/')[1] || '');
  const businessId = selectedBusiness?.id;

  const {
    data: templates,
    isLoading: isTemplatesLoading
  } = useQuery<LegalDocumentTemplate[]>({
    queryKey: ["/api/legal-document-templates/type", type],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/legal-document-templates/type/${type}`);
      return await res.json();
    },
    enabled: !!type
  });

  // Form schema will be dynamically created based on selected template
  const [formSchema, setFormSchema] = useState(z.object({}));
  
  // Dynamic form
  const form = useForm<any>({
    resolver: zodResolver(formSchema),
    defaultValues: {}
  });
  
  // Create document mutation
  const createDocumentMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!businessId || !selectedTemplate) {
        throw new Error("Business or template not selected");
      }
      
      const payload = {
        templateId: selectedTemplate.id,
        documentName: data.documentName,
        formData: data
      };
      
      const res = await apiRequest(
        "POST", 
        `/api/businesses/${businessId}/legal-documents/generate`, 
        payload
      );
      
      return await res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Document Created",
        description: "Your legal document has been generated successfully.",
      });
      setLocation(`/legal/view/${data.id}`);
    },
    onError: (error: Error) => {
      setIsGenerating(false);
      toast({
        title: "Failed to generate document",
        description: error.message,
        variant: "destructive",
      });
    }
  });

  useEffect(() => {
    // Reset form when template changes
    if (selectedTemplate) {
      // Create a dynamic form schema based on required fields
      const fieldsSchema: Record<string, z.ZodTypeAny> = {};
      
      // Add document name field
      fieldsSchema.documentName = z.string()
        .min(1, "Document name is required")
        .max(100, "Document name must be less than 100 characters");
      
      // Add template-specific fields
      if (Array.isArray(selectedTemplate.requiredFields)) {
        selectedTemplate.requiredFields.forEach(field => {
          fieldsSchema[field] = z.string().min(1, `${field} is required`);
        });
      }
      
      setFormSchema(z.object(fieldsSchema));
      
      // Reset form with default values
      const defaultValues: Record<string, string> = { documentName: "" };
      if (Array.isArray(selectedTemplate.requiredFields)) {
        selectedTemplate.requiredFields.forEach(field => {
          defaultValues[field] = "";
        });
      }
      
      form.reset(defaultValues);
    }
  }, [selectedTemplate, form]);

  const handleSelectTemplate = (template: LegalDocumentTemplate) => {
    setSelectedTemplate(template);
    setIsFormStep(true);
  };

  const onSubmit = async (data: any) => {
    setIsGenerating(true);
    createDocumentMutation.mutate(data);
  };

  const goBack = () => {
    if (isFormStep) {
      setIsFormStep(false);
      setSelectedTemplate(null);
    } else {
      setLocation("/legal");
    }
  };

  // Format field label
  const formatFieldLabel = (field: string) => {
    return field
      .replace(/([A-Z])/g, ' $1') // Insert a space before all capital letters
      .replace(/^./, str => str.toUpperCase()); // Capitalize the first letter
  };

  if (!businessId) {
    return (
      <DashboardShell>
        <DashboardHeader 
          heading="Create Legal Document" 
          text="Generate a customized legal document for your business" 
        />
        <div className="rounded-lg border p-8 text-center">
          <h2 className="text-xl font-semibold mb-2">No Business Selected</h2>
          <p className="text-muted-foreground mb-4">
            Please select a business to create a legal document.
          </p>
          <Button onClick={() => setLocation("/businesses")}>
            Select Business
          </Button>
        </div>
      </DashboardShell>
    );
  }

  if (isTemplatesLoading) {
    return (
      <DashboardShell>
        <DashboardHeader 
          heading="Create Legal Document" 
          text="Generate a customized legal document for your business" 
        />
        <div className="flex items-center justify-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <p className="ml-2">Loading templates...</p>
        </div>
      </DashboardShell>
    );
  }



  return (
    <DashboardShell>
      <Helmet>
        <title>Create Legal Document | ZentroBooks</title>
        <meta name="description" content="Generate a customized legal document for your business" />
      </Helmet>
      
      <DashboardHeader 
        heading="Create Legal Document" 
        text="Generate a customized legal document for your business" 
      >
        <Button variant="outline" onClick={goBack}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Back
        </Button>
      </DashboardHeader>

      {!isFormStep ? (
        <>
          <h2 className="text-2xl font-bold mb-6">Select a {type} Template</h2>
          
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {templates && templates.map((template) => (
              <Card 
                key={template.id}
                className="cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => handleSelectTemplate(template)}
              >
                <CardHeader>
                  <CardTitle>{template.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground">{template.description}</p>
                </CardContent>
                <CardFooter>
                  <Button variant="ghost" className="w-full">
                    Select Template
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          {templates && templates.length === 0 && (
            <div className="rounded-lg border p-8 text-center">
              <h2 className="text-xl font-semibold mb-2">No Templates Available</h2>
              <p className="text-muted-foreground mb-4">
                There are no templates available for this document type.
              </p>
              <Button onClick={() => setLocation("/legal")}>
                Go Back
              </Button>
            </div>
          )}
        </>
      ) : selectedTemplate && (
        <>
          <h2 className="text-2xl font-bold mb-2">{selectedTemplate.name}</h2>
          <p className="text-muted-foreground mb-6">{selectedTemplate.description}</p>
          
          <div className="rounded-lg border p-6">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <FormField
                  control={form.control}
                  name="documentName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Document Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter document name" {...field} />
                      </FormControl>
                      <FormDescription>
                        Give your document a descriptive name
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                {/* Dynamic form fields based on template requirements */}
                {selectedTemplate.requiredFields && Array.isArray(selectedTemplate.requiredFields) && 
                  selectedTemplate.requiredFields.map((fieldName) => (
                    <FormField
                      key={fieldName}
                      control={form.control}
                      name={fieldName}
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{formatFieldLabel(fieldName)}</FormLabel>
                          <FormControl>
                            {fieldName.toLowerCase().includes('description') || 
                             fieldName.toLowerCase().includes('details') ? (
                              <Textarea 
                                placeholder={`Enter ${fieldName.toLowerCase()}`} 
                                className="min-h-[100px]"
                                {...field} 
                              />
                            ) : (
                              <Input 
                                placeholder={`Enter ${fieldName.toLowerCase()}`} 
                                {...field} 
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))
                }
                
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={goBack}>
                    Back
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={isGenerating || createDocumentMutation.isPending}
                  >
                    {(isGenerating || createDocumentMutation.isPending) && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Generate Document
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        </>
      )}
    </DashboardShell>
  );
}
